import { Link } from 'react-router-dom';
import { LegalPageLayout } from './LegalPageLayout';

const EFFECTIVE_DATE = 'March 1, 2026';
const VERSION = '1.0';

export default function TermsOfService() {
  return (
    <LegalPageLayout>
      <h1>Terms of Service</h1>
      <p><strong>No Doubles Golf Co.</strong></p>
      <p><strong>Version:</strong> {VERSION}</p>
      <p><strong>Effective Date:</strong> {EFFECTIVE_DATE}</p>
      <p><strong>Last Updated:</strong> {EFFECTIVE_DATE}</p>
      <p>
        These Terms of Service ("Terms") govern your access to and use of the No Doubles Golf Co.
        mobile applications, website, and related services (collectively, the "Service"), operated
        by No Doubles Golf Co., an Arizona limited liability company ("No Doubles," "we," "us," or
        "our").
      </p>
      <p>
        By accessing or using the Service, you agree to be bound by these Terms. If you do not
        agree, do not use the Service.
      </p>

      <hr />

      <h2>1. Acceptance of Terms</h2>
      <p>
        By creating an account, downloading the application, or accessing any part of the Service,
        you acknowledge that you have read, understood, and agree to be bound by these Terms, our{' '}
        <Link to="/privacy">Privacy Policy</Link>, and all applicable addenda referenced herein.
      </p>
      <p>
        Your continued use of the Service after any modification to these Terms constitutes
        acceptance of the modified Terms. If you do not agree to any modification, your sole remedy
        is to discontinue use of the Service.
      </p>

      <hr />

      <h2>2. Eligibility and Age Requirements</h2>
      <h3>2.1 Minimum Age</h3>
      <p>
        The Service is intended for users age 13 and older. If you are under 13 years of age, you
        may not use the Service under any circumstances.
      </p>
      <h3>2.2 Users Under 18</h3>
      <p>
        If you are between 13 and 17 years of age, you may use the Service only with the verified
        consent and ongoing supervision of a parent or legal guardian. Your parent or legal guardian
        must review and accept these Terms on your behalf and agrees to be bound by these Terms with
        respect to your use of the Service. See the{' '}
        <Link to="/terms/parental-consent">Parental Consent Addendum</Link> for specific
        requirements.
      </p>
      <h3>2.3 Parental Responsibility</h3>
      <p>
        The parent or legal guardian who consents to a minor's use of the Service assumes full
        responsibility for:
      </p>
      <ul>
        <li>The minor's compliance with these Terms</li>
        <li>All activity conducted through the minor's account</li>
        <li>
          Any physical activity the minor undertakes based on content accessed through the Service
        </li>
        <li>All fees incurred through the minor's account</li>
      </ul>
      <h3>2.4 Representations</h3>
      <p>
        By using the Service, you represent that you are at least 13 years old and, if under 18,
        that your parent or legal guardian has consented to your use and accepted these Terms.
      </p>

      <hr />

      <h2>3. Description of Services</h2>
      <p>No Doubles provides a structured golf practice system that includes:</p>
      <ul>
        <li>AI-assisted video analysis of golf swing mechanics</li>
        <li>Drill recommendations and structured practice plans</li>
        <li>Mobility screening and movement exercises</li>
        <li>Performance tracking and progress monitoring</li>
        <li>Subscription-based premium features</li>
      </ul>
      <p>
        The Service is a <strong>practice tool</strong>. It is not a substitute for in-person golf
        instruction, medical advice, physical therapy, or professional coaching. The Service uses
        technology to deliver structured improvement — it does not provide professional coaching
        services, medical diagnosis, or therapeutic treatment.
      </p>
      <h3>3.1 Coach Marketplace (When Available)</h3>
      <p>
        No Doubles may offer a marketplace connecting users with independent golf instructors
        ("Coaches"). When this feature is available:
      </p>
      <ul>
        <li>Coaches are independent contractors, not employees or agents of No Doubles.</li>
        <li>
          No Doubles does not guarantee the qualifications, competence, or suitability of any Coach.
        </li>
        <li>
          No Doubles does not supervise, direct, or control the instruction provided by Coaches.
        </li>
        <li>
          Any engagement between you and a Coach is a separate relationship. No Doubles is not a
          party to that relationship.
        </li>
        <li>
          No Doubles disclaims all liability for the acts, omissions, instruction, or conduct of
          Coaches.
        </li>
        <li>
          All Coaches are bound by separate agreements with No Doubles that include indemnification
          obligations, codes of conduct, and, where applicable, insurance requirements. These Coach
          agreements are independent of your relationship with No Doubles.
        </li>
      </ul>

      <hr />

      <h2>4. AI-Generated Content Disclosure</h2>
      <h3>4.1 Nature of AI Outputs</h3>
      <p>
        The Service uses artificial intelligence and machine learning models to analyze video
        uploads, assess movement patterns, and generate coaching feedback, drill recommendations,
        and performance observations (collectively, "AI Outputs").
      </p>
      <p>
        <strong>
          AI Outputs are generated by automated systems. They are not reviewed by a human
          instructor, coach, medical professional, or certified trainer before delivery to you.
        </strong>
      </p>
      <h3>4.2 Limitations of AI</h3>
      <p>You acknowledge and agree that:</p>
      <ul>
        <li>
          AI Outputs are <strong>informational and educational only</strong>. They do not constitute
          professional instruction, medical advice, physical therapy, or personalized coaching.
        </li>
        <li>
          AI systems may produce inaccurate, incomplete, inconsistent, or inappropriate outputs.
          This is an inherent characteristic of AI technology, not a defect.
        </li>
        <li>
          AI Outputs are generated based on limited information (your uploaded video and
          user-provided data) and may not account for your physical condition, injury history,
          fitness level, or individual biomechanical limitations.
        </li>
        <li>
          No Doubles does not warrant the accuracy, reliability, completeness, or suitability of
          any AI Output for any purpose.
        </li>
        <li>
          You are solely responsible for evaluating AI Outputs and deciding whether and how to use
          them.
        </li>
      </ul>
      <h3>4.3 No Reliance</h3>
      <p>
        You agree not to rely on AI Outputs as a substitute for professional instruction, medical
        evaluation, or physical therapy. If you have any physical condition, injury, limitation, or
        concern about your ability to perform physical activity safely, consult a qualified
        healthcare provider before using the Service.
      </p>
      <p>
        See the <Link to="/terms/ai-disclosure">AI Disclosure Addendum</Link> for additional detail.
      </p>

      <hr />

      <h2>5. No Medical Advice</h2>
      <h3>5.1 Not a Healthcare Service</h3>
      <p>
        The Service does not provide medical advice, diagnosis, or treatment. Nothing in the
        Service — including AI Outputs, drill recommendations, mobility screenings, and movement
        assessments — is intended to diagnose, treat, cure, or prevent any medical condition,
        physical injury, or disease.
      </p>
      <h3>5.2 Mobility Screening Disclaimer</h3>
      <p>
        Mobility screening features within the Service are general movement assessments designed to
        inform your practice plan. They are <strong>not</strong> medical evaluations, physical
        therapy assessments, or clinical screenings.
      </p>
      <h3>5.3 Consult Your Doctor</h3>
      <p>
        You should consult a qualified healthcare provider before beginning any new exercise
        program. This is especially important if you:
      </p>
      <ul>
        <li>Have a pre-existing injury or medical condition</li>
        <li>Have undergone recent surgery</li>
        <li>Experience pain during physical activity</li>
        <li>Are pregnant</li>
        <li>Have been advised by a medical professional to limit physical activity</li>
        <li>
          Are taking medication that may affect your balance, coordination, or physical performance
        </li>
      </ul>

      <hr />

      <h2>6. Assumption of Risk</h2>
      <h3>6.1 Physical Activity Risk</h3>
      <p>
        Golf practice, swing training, mobility exercises, and related physical activities involve
        inherent risks of physical injury, including but not limited to muscle strains, sprains,
        joint stress, overuse injuries, aggravation of pre-existing conditions, fatigue-related
        injury, and falls.
      </p>
      <p>
        <strong>
          You voluntarily assume all risks associated with your use of the Service and any physical
          activity you undertake in connection with the Service, whether or not such activity was
          recommended by the Service.
        </strong>
      </p>
      <h3>6.2 Personal Responsibility</h3>
      <p>You are solely responsible for:</p>
      <ul>
        <li>Assessing your own fitness and physical capacity before performing any exercise</li>
        <li>Performing exercises with proper form and within your physical limitations</li>
        <li>Stopping any activity that causes pain, discomfort, or unusual physical symptoms</li>
        <li>Ensuring you have adequate space, equipment, and conditions to exercise safely</li>
        <li>Obtaining medical clearance before beginning any new physical activity program</li>
      </ul>
      <h3>6.3 Waiver and Release</h3>
      <p>
        To the maximum extent permitted by applicable law, you release, waive, and discharge No
        Doubles, its officers, directors, members, employees, agents, and affiliates from any and
        all claims, demands, causes of action, and liabilities arising from or related to any
        injury, illness, disability, death, or loss or damage to person or property that you
        sustain in connection with your use of the Service. This waiver applies regardless of
        whether such injury results from the negligence of No Doubles (except to the extent such
        waiver is prohibited by applicable law).
      </p>
      <h3>6.4 Minor Users</h3>
      <p>
        If you are the parent or legal guardian of a minor user, you accept and assume the risks
        described in this Section on behalf of the minor to the maximum extent permitted by law.
      </p>

      <hr />

      <h2>7. No Professional Coaching Relationship</h2>
      <p>
        Your use of the Service does not create any professional relationship between you and No
        Doubles, including coach-client, trainer-trainee, instructor-student, medical
        provider-patient, or fiduciary relationships of any kind. The Service provides automated
        tools and structured practice content — it does not provide professional services.
      </p>

      <hr />

      <h2>8. Subscription and Payment Terms</h2>
      <h3>8.1 Subscription Plans</h3>
      <p>
        The Service may offer subscription-based access to premium features. Subscription plans,
        pricing, and features are described in the Service and may change from time to time.
      </p>
      <h3>8.2 Billing</h3>
      <p>Your subscription will be billed through the platform where you originally subscribed:</p>
      <ul>
        <li><strong>Apple App Store</strong> — Billed through your Apple ID account via Apple In-App Purchase.</li>
        <li><strong>Google Play Store</strong> — Billed through your Google account via Google Play Billing.</li>
        <li><strong>Web (Stripe)</strong> — Billed directly through Stripe.</li>
      </ul>
      <h3>8.3 Auto-Renewal</h3>
      <p>
        <strong>
          Your subscription will automatically renew at the end of each billing period (monthly or
          annual, as applicable) unless you cancel before the renewal date.
        </strong>{' '}
        You will be charged the then-current subscription price at the beginning of each renewal
        period.
      </p>
      <h3>8.4 Cancellation</h3>
      <p>
        You may cancel your subscription at any time. Cancellation takes effect at the end of the
        current billing period. You will retain access to premium features until the end of your
        paid period. No partial refunds are provided for unused portions of a billing period,
        except as required by applicable law or platform policies.
      </p>
      <p>
        See the <Link to="/terms/subscription">Subscription Addendum</Link> for additional detail.
      </p>

      <hr />

      <h2>9. User Content and Video License</h2>
      <h3>9.1 Your Content</h3>
      <p>
        You retain ownership of all content you upload, submit, or transmit through the Service,
        including video recordings of your golf swing ("User Content").
      </p>
      <h3>9.2 License Grant</h3>
      <p>
        By uploading User Content, you grant No Doubles a worldwide, non-exclusive, royalty-free
        license (sublicensable solely to service providers acting on our behalf) to use, reproduce,
        process, analyze, store, and display your User Content solely for the purposes of:
      </p>
      <ul>
        <li>Providing the Service to you (including AI analysis and feedback generation)</li>
        <li>Maintaining and improving the Service</li>
        <li>Generating anonymized, aggregated, de-identified data for product improvement and research</li>
      </ul>
      <p>
        This license survives termination of your account only with respect to anonymized,
        aggregated data that does not identify you. This license is transferable solely in
        connection with a merger, acquisition, or sale of all or substantially all of No Doubles'
        assets. All identifiable User Content will be deleted in accordance with our Privacy Policy
        upon account termination, subject to any legal retention obligations.
      </p>
      <h3>9.3 No Training on User Data</h3>
      <p>
        No Doubles does not use your identifiable User Content — including your swing videos,
        movement data, or personal information — to train AI or machine learning models.
        Anonymized, aggregated, de-identified data that cannot be traced to any individual may be
        used for general product improvement and research purposes.
      </p>

      <hr />

      <h2>10. Intellectual Property</h2>
      <p>
        The Service, including all software, algorithms, AI models, drill libraries, coaching
        frameworks, user interfaces, graphics, text, and data compilations are owned by or licensed
        to No Doubles and are protected by copyright, trademark, patent, trade secret, and other
        intellectual property laws.
      </p>

      <hr />

      <h2>11. Acceptable Use</h2>
      <p>
        You agree not to use the Service for any unlawful purpose, impersonate any person, upload
        harmful content, interfere with the Service, or use automated systems without permission.
        We may suspend or terminate your account for violations.
      </p>

      <hr />

      <h2>12. Third-Party Services</h2>
      <p>
        The Service may integrate with or contain links to third-party services. These are governed
        by their own terms and privacy policies. No Doubles is not liable for any loss arising from
        your interaction with third-party services.
      </p>

      <hr />

      <h2>13. Data and Privacy</h2>
      <p>
        Your use of the Service is also governed by our{' '}
        <Link to="/privacy">Privacy Policy</Link>. The Privacy Policy describes how we collect,
        use, store, and protect your personal information, including video data, movement
        telemetry, and account information.
      </p>
      <h3>13.3 Biometric Data Notice</h3>
      <p>
        The Service processes video recordings to analyze body position and movement patterns.
        Depending on your jurisdiction, this processing may involve data classified as "biometric
        data" or "biometric identifiers" under applicable law. Before processing any video that may
        generate biometric data, we will obtain your informed consent as required by applicable law.
      </p>

      <hr />

      <h2>14. Disclaimer of Warranties</h2>
      <p>
        <strong>
          THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, EITHER
          EXPRESS OR IMPLIED.
        </strong>
      </p>
      <p>
        TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW, NO DOUBLES DISCLAIMS ALL WARRANTIES,
        INCLUDING BUT NOT LIMITED TO IMPLIED WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR
        PURPOSE, AND NON-INFRINGEMENT.
      </p>
      <p>
        <strong>
          NO DOUBLES DOES NOT WARRANT OR GUARANTEE THAT USE OF THE SERVICE WILL RESULT IN
          IMPROVEMENT OF YOUR GOLF SCORE, SWING MECHANICS, PHYSICAL PERFORMANCE, OR ANY OTHER
          OUTCOME.
        </strong>
      </p>

      <hr />

      <h2>15. Limitation of Liability</h2>
      <p>
        TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW, NO DOUBLES' TOTAL AGGREGATE LIABILITY
        TO YOU FOR ALL CLAIMS ARISING OUT OF OR RELATED TO THESE TERMS OR THE SERVICE SHALL NOT
        EXCEED THE GREATER OF: (A) THE AMOUNTS YOU HAVE PAID TO NO DOUBLES IN THE TWELVE (12)
        MONTHS IMMEDIATELY PRECEDING THE EVENT GIVING RISE TO THE CLAIM, OR (B) ONE HUNDRED
        DOLLARS ($100.00 USD).
      </p>

      <hr />

      <h2>16. Indemnification</h2>
      <p>
        You agree to indemnify, defend, and hold harmless No Doubles from and against any claims
        arising out of your use of the Service, your violation of these Terms, or your User Content.
        If you are the parent or legal guardian of a minor user, this obligation extends to all
        claims arising from the minor's use.
      </p>

      <hr />

      <h2>17. Dispute Resolution: Binding Arbitration and Class Action Waiver</h2>
      <p>
        <strong>
          PLEASE READ THIS SECTION CAREFULLY. IT AFFECTS YOUR LEGAL RIGHTS, INCLUDING YOUR RIGHT
          TO FILE A LAWSUIT IN COURT AND TO HAVE A JURY TRIAL.
        </strong>
      </p>
      <p>
        You and No Doubles agree that any dispute will be resolved through final and binding
        individual arbitration administered by the American Arbitration Association.
      </p>
      <h3>17.3 Class Action Waiver</h3>
      <p>
        <strong>
          YOU AND NO DOUBLES AGREE THAT EACH MAY BRING CLAIMS AGAINST THE OTHER ONLY IN YOUR OR
          ITS INDIVIDUAL CAPACITY, AND NOT AS A PLAINTIFF OR CLASS MEMBER IN ANY PURPORTED CLASS,
          CONSOLIDATED, OR REPRESENTATIVE PROCEEDING.
        </strong>
      </p>
      <h3>17.5 Opt-Out Right</h3>
      <p>
        You may opt out of this arbitration agreement by sending written notice within 30 days of
        first accepting these Terms. The opt-out period begins on the date you create your account
        and affirmatively accept these Terms (by checking the Terms acceptance box during sign-up).
        Your notice must include your name, the email address associated with your account, and a
        clear statement that you wish to opt out.
      </p>

      <hr />

      <h2>18. Termination</h2>
      <h3>18.1 Termination by You</h3>
      <p>
        You may terminate your account at any time through your account settings. Termination does
        not entitle you to a refund of prepaid subscription fees, except as required by applicable
        law.
      </p>
      <h3>18.2 Termination by No Doubles</h3>
      <p>
        We may suspend or terminate your account if we reasonably believe you have violated these
        Terms. For non-material breaches, we will provide you with 7 days' written notice and an
        opportunity to cure the breach before termination, except where the breach involves illegal
        activity, fraud, abuse of other users, or conduct that poses a safety risk. We may
        immediately suspend or terminate without notice for material breaches, including violations
        of Sections 11 (Acceptable Use) or 10.3 (Restrictions).
      </p>

      <hr />

      <h2>19. Changes to Terms</h2>
      <p>
        We may modify these Terms at any time. Material changes will be communicated through the
        Service. For material changes that affect your rights under the arbitration agreement or
        subscription terms, we will provide at least 30 days' notice before the changes take effect.
      </p>

      <hr />

      <h2>20. Governing Law and Jurisdiction</h2>
      <h3>20.1 Governing Law</h3>
      <p>
        These Terms are governed by the laws of the State of Arizona, without regard to its conflict
        of law principles.
      </p>
      <h3>20.2 Jurisdiction</h3>
      <p>
        For disputes not subject to arbitration, you consent to exclusive jurisdiction in Maricopa
        County, Arizona.
      </p>
      <h3>20.3 Geographic Availability</h3>
      <p>
        The Service is currently available to users in the United States and Canada. The Service is
        not directed to users in the European Union, European Economic Area, or United Kingdom. If
        you access the Service from outside the United States or Canada, you do so at your own risk
        and are responsible for compliance with local laws. We reserve the right to restrict access
        to the Service from any jurisdiction.
      </p>
      <h3>20.4 Canadian Users</h3>
      <p>
        If you are a resident of Canada, certain consumer protection laws in your province may
        apply to you and may override certain provisions of these Terms. Nothing in these Terms is
        intended to limit rights that cannot be waived under applicable Canadian federal or
        provincial law.
      </p>

      <hr />

      <h2>21. Miscellaneous</h2>
      <p>
        These Terms, together with the{' '}
        <Link to="/privacy">Privacy Policy</Link> and all addenda referenced herein, constitute the
        entire agreement between you and No Doubles regarding the Service.
      </p>
      <h3>21.6 Force Majeure</h3>
      <p>
        No Doubles shall not be liable for any delay or failure to perform resulting from causes
        outside its reasonable control, including natural disasters, acts of war, terrorism,
        epidemics, power failures, internet disruptions, or government actions. If a force majeure
        event prevents No Doubles from providing the Service for more than 30 consecutive days,
        active subscribers will receive a pro-rata credit or extension of their subscription period
        for the duration of the interruption.
      </p>

      <hr />

      <h2>22. No Guarantee of Results</h2>
      <p>
        <strong>No Doubles does not guarantee that you will achieve any particular result.</strong>{' '}
        Individual results vary based on many factors including practice consistency, physical
        ability, and adherence to recommended practice plans.
      </p>

      <hr />

      <h2>23. Billing Dispute Assistance</h2>
      <p>
        If you have a billing dispute related to a subscription purchased through Apple or Google,
        No Doubles will make reasonable efforts to assist you in navigating the applicable
        platform's refund process, even when we are unable to issue the refund directly. Contact us
        and we will help direct you to the appropriate resolution channel.
      </p>

      <hr />

      <h2>Contact Information</h2>
      <p>No Doubles Golf Co.</p>
      <p>Email: support@nodoublesgolf.com</p>

      <hr />

      <h2>Key Terms Summary</h2>
      <p>
        <em>This summary is provided for convenience. The full Terms above govern your use of the Service.</em>
      </p>
      <table>
        <thead>
          <tr><th>Topic</th><th>What It Means</th></tr>
        </thead>
        <tbody>
          <tr><td><strong>What No Doubles is</strong></td><td>A structured golf practice system that uses technology to deliver drills, analysis, and progress tracking. Not medical advice. Not professional coaching.</td></tr>
          <tr><td><strong>AI outputs</strong></td><td>Generated by automated systems, not reviewed by humans. May be inaccurate. Use your own judgment.</td></tr>
          <tr><td><strong>Physical activity</strong></td><td>Golf practice involves risk of injury. You are responsible for your own physical safety. Consult a doctor before starting.</td></tr>
          <tr><td><strong>Your videos</strong></td><td>You own them. We use them only to provide the Service. We don't use them to train AI models.</td></tr>
          <tr><td><strong>Subscriptions</strong></td><td>Auto-renew unless you cancel. Cancel through your App Store, Play Store, or account settings.</td></tr>
          <tr><td><strong>Minors (13–17)</strong></td><td>Parental consent required. Under 13 cannot use the Service.</td></tr>
          <tr><td><strong>Disputes</strong></td><td>Resolved by individual arbitration, not court. You can opt out within 30 days of sign-up. No class actions.</td></tr>
          <tr><td><strong>Data</strong></td><td>See our Privacy Policy for how we handle your information. You can delete your account and data anytime.</td></tr>
          <tr><td><strong>Liability</strong></td><td>Capped at what you paid us in the last 12 months.</td></tr>
          <tr><td><strong>Termination</strong></td><td>You can leave anytime. We can terminate for violations (with cure period for minor issues).</td></tr>
        </tbody>
      </table>

      <hr />

      <p><strong>Addenda (incorporated by reference):</strong></p>
      <ul>
        <li><Link to="/terms/subscription">Subscription Addendum</Link></li>
        <li><Link to="/terms/ai-disclosure">AI Disclosure Addendum</Link></li>
        <li><Link to="/terms/parental-consent">Parental Consent Addendum</Link></li>
      </ul>
    </LegalPageLayout>
  );
}
